<template>
  <div class="profile-modal-overlay" @click.self="close">
    <div
      class="profile-modal"
      role="dialog"
      aria-modal="true"
      :aria-label="action === 'offer' ? 'Counteroffer' : 'Withdrawal'"
    >
      <button
        type="button"
        class="profile-modal-close"
        aria-label="Close"
        :disabled="loading"
        @click="close"
      >
        ×
      </button>

      <template v-if="action === 'offer' && offer">
        <div class="title medium profile-modal-title">
          Counteroffer #{{ offer.id }}
        </div>

        <div class="profile-modal-prices">
          <div class="profile-modal-price">
            <span>Your price</span>
            <strong> {{ money(offer.total_amount) }} {{ currency }} </strong>
          </div>

          <div class="profile-modal-price">
            <span>New offer</span>
            <strong class="profile-modal-accent">
              {{ money(offer.counter_offer_amount) }}
              {{ currency }}
            </strong>
          </div>
        </div>

        <div v-if="offer.admin_note" class="profile-modal-note">
          {{ offer.admin_note }}
        </div>

        <div class="profile-modal-actions">
          <button
            type="button"
            class="button profile-modal-button"
            :disabled="loading"
            @click="$emit('confirm', 'accept')"
          >
            {{ loading ? 'Processing...' : 'Accept' }}
          </button>

          <button
            type="button"
            class="button black profile-modal-button"
            :disabled="loading"
            @click="$emit('confirm', 'decline')"
          >
            Decline
          </button>
        </div>
      </template>

      <template v-else>
        <div class="title medium profile-modal-title">Confirm withdrawal</div>

        <div class="profile-modal-withdraw-amount">
          {{ amount }} {{ currency }}
        </div>

        <div class="profile-modal-actions">
          <button
            type="button"
            class="button profile-modal-button"
            :disabled="loading"
            @click="$emit('confirm', 'withdraw')"
          >
            {{ loading ? 'Processing...' : 'Confirm' }}
          </button>

          <button
            type="button"
            class="button black profile-modal-button"
            :disabled="loading"
            @click="close"
          >
            Cancel
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProfileActionModal',

  props: {
    action: {
      type: String,
      required: true,
    },

    offer: {
      type: Object,
      default: null,
    },

    amount: {
      type: String,
      default: '',
    },

    currency: {
      type: String,
      default: 'EUR',
    },

    loading: {
      type: Boolean,
      default: false,
    },
  },

  mounted() {
    document.addEventListener('keydown', this.onKeydown)
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown)
  },

  methods: {
    close() {
      if (!this.loading) {
        this.$emit('close')
      }
    },

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.close()
      }
    },

    money(value) {
      const number = Number(value)

      return Number.isFinite(number) ? number.toFixed(2) : '0.00'
    },
  },
}
</script>

<style scoped>
.profile-modal-overlay,
.profile-modal-overlay * {
  box-sizing: border-box;
}

.profile-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(0, 0, 0, 0.82);
}

.profile-modal {
  position: relative;
  width: 100%;
  max-width: 420px;
  max-height: calc(100vh - 32px);
  overflow-x: hidden;
  overflow-y: auto;
  padding: 30px 28px;
  background: #211919;
  border: 1px solid #554040;
  color: #fff;
}

.profile-modal-close {
  position: absolute;
  top: 10px;
  right: 14px;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #bbb;
  font-family: inherit;
  font-size: 25px;
  cursor: pointer;
}

.profile-modal-close:hover {
  color: #fff;
}

.profile-modal-title {
  margin: 0 30px 22px 0;
  color: #fff;
}

.profile-modal-prices {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 14px;
  margin-bottom: 24px;
}

.profile-modal-price {
  min-width: 0;
}

.profile-modal-price span {
  display: block;
  margin-bottom: 6px;
  color: #b9aaaa;
  font-family: 'Open Sans', Arial, sans-serif;
  font-size: 12px;
}

.profile-modal-price strong {
  display: block;
  color: #fff;
  font-family: 'Open Sans', Arial, sans-serif;
  font-size: 15px;
  font-weight: 700;
  overflow-wrap: anywhere;
}

.profile-modal-price .profile-modal-accent {
  color: #e15d40;
}

.profile-modal-note {
  margin-bottom: 20px;
  color: #b9aaaa;
  font-family: 'Open Sans', Arial, sans-serif;
  font-size: 12px;
  line-height: 1.6;
}

.profile-modal-withdraw-amount {
  margin: 12px 0 25px;
  color: #e15d40;
  font-family: 'Open Sans', Arial, sans-serif;
  font-size: 24px;
  font-weight: 700;
}

.profile-modal-actions {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 10px;
  width: 100%;
  min-width: 0;
}

.profile-modal-actions .profile-modal-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  min-height: 42px;
  height: auto;
  margin: 0;
  padding: 9px 6px;
  border-radius: 0;
  text-align: center;
  font-family: 'Bebas Neue', Impact, sans-serif;
  font-size: 17px;
  font-weight: 400;
  line-height: 1.2;
  white-space: normal;
  overflow-wrap: anywhere;
  cursor: pointer;
}

.profile-modal-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 480px) {
  .profile-modal {
    padding: 27px 18px 20px;
  }

  .profile-modal-actions {
    gap: 8px;
  }

  .profile-modal-actions .profile-modal-button {
    font-size: 15px;
  }
}
</style>
